import { useState } from "react";
import { AccessibilityInfo, Text, View } from "react-native";
import { useTranslation } from "react-i18next";
import { unmetPasswordRules } from "@heraion/shared";
import { Button } from "../primitives/Button";
import { FieldLabel } from "../primitives/FieldLabel";
import { TextField } from "../primitives/TextField";
import { AgeDeclarationCheckbox } from "./AgeDeclarationCheckbox";
import { NewPasswordFields, passwordMatch } from "./NewPasswordFields";
import { authFormStyles } from "./authFormStyles";
import type { UiTheme } from "../theme";

// PROF-3 + PROF-1 — la porta per chi entra su INVITO, montata su mobile e web.
//
// ⛔ Non è un secondo login: ci si arriva SOLO dalla sfida `NEW_PASSWORD_REQUIRED`
// che Cognito manda al primo accesso di un account creato da admin. Chi la vede
// ha già dimostrato di possedere la password temporanea — qui non si autentica,
// si finisce di nascere.
//
// ⭐ Perché chiede anche il NOME, che Cognito non chiede (`requiredAttributes`
// è vuoto, misurato l'1/09): un invitato non ha riga `PROFILE`, e la PUT del
// profilo pretende un `name` — è l'unico campo dello schema senza default
// (`schemas/profile.ts:129`; `unitsPreference` ha `.default("kg")`). Decisione
// sua dell'1/09: si CHIEDE nel momento in cui la persona sta già scrivendo,
// invece di derivarlo dall'email. Un `l.bianchi83` dedotto è un segnaposto che
// sembra una scelta, e nessuno lo corregge perché nessuno lo nota.
export type WelcomeFormProps = {
  theme: UiTheme;
  /** Completa la sfida E crea il profilo. Rigetta con copia già tradotta. */
  onComplete: (params: {
    newPassword: string;
    name: string;
    /** #175: when the athlete ticked the age declaration (device clock). */
    ageDeclaredAt: string;
  }) => Promise<void>;
};

export function WelcomeForm({ theme, onComplete }: WelcomeFormProps) {
  const { t } = useTranslation();
  const styles = makeStyles(theme);
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  // DS-67 — UN interruttore per ENTRAMBI i campi password. Lo stato resta al
  // form: è lui che sa quando si è premuto «invia».
  const [revealed, setRevealed] = useState(false);
  // #175: when the age box was ticked (AgeDeclarationCheckbox dates it).
  const [ageTickedAt, setAgeTickedAt] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // ⛔ Quale campo è in errore, non «il form è in errore». Marcare tutti e tre
  // per una password corta direbbe all'atleta che anche il suo nome è
  // sbagliato — e `invalid` senza una riga che spiega sarebbe solo-colore.
  const [badField, setBadField] = useState<"name" | "password" | "confirm" | null>(
    null,
  );

  const fail = (field: typeof badField, message: string) => {
    setBadField(field);
    setError(message);
    // `accessibilityLiveRegion` è Android + web soltanto (r-n-w lo mappa su
    // aria-live); su iOS il testo comparirebbe senza che nessuno lo dica, e su
    // questo schermo il messaggio d'errore è l'UNICA spiegazione di perché il
    // bottone non ha fatto niente. Stesso rimedio del fratello che porta lo
    // stesso vincolo, `Logger/ExerciseVisualPager.tsx:173-176`.
    AccessibilityInfo.announceForAccessibility(message);
  };

  const onSubmit = async () => {
    setError(null);
    setBadField(null);
    // ⛔ Chi ha rivelato le password per confrontarle non deve restare con
    // quelle a schermo mentre il giro di rete gira: se `onComplete` fallisce,
    // il form resta a video e la password resterebbe leggibile a chiunque
    // passi. Una riga, e toglie una finestra che non serviva a nessuno.
    setRevealed(false);

    if (name.trim().length === 0) {
      return fail("name", t("auth.welcome.nameRequired"));
    }
    // La policy vive in @heraion/shared perché è la configurazione del POOL,
    // non una scelta di questo schermo. Validare qui invece di lasciar
    // rispondere Cognito evita un giro di rete e, soprattutto, evita che
    // l'atleta legga `InvalidPasswordException` in inglese.
    if (unmetPasswordRules(password).length > 0) {
      return fail("password", t("auth.passwordHint"));
    }
    if (passwordMatch(password, confirm) !== "match") {
      return fail("confirm", t("auth.passwordMismatch"));
    }

    // #175: the declaration is the one thing this door cannot skip.
    if (ageTickedAt === null) return fail(null, t("auth.welcome.ageRequired"));

    setBusy(true);
    try {
      await onComplete({
        newPassword: password,
        name: name.trim(),
        ageDeclaredAt: ageTickedAt,
      });
      // ⛔ Nessun setBusy(false) qui: se va a buon fine questo schermo sparisce
      // (l'albero passa a «dentro»), e riabilitare i campi su un componente che
      // si sta smontando è il warning di React su un albero morto.
    } catch (err) {
      setBusy(false);
      // Passa da `fail` come gli altri tre: un fallimento della rete si legge e
      // si SENTE come uno di validazione. Nessun campo è colpevole, quindi
      // nessun campo si marca.
      fail(null, err instanceof Error ? err.message : String(err));
    }
  };

  return (
    <View style={styles.wrap}>
      <Text style={styles.title}>{t("auth.welcome.title")}</Text>
      <Text style={styles.help}>{t("auth.welcome.help")}</Text>

      <FieldLabel theme={theme} style={styles.label}>{t("auth.welcome.name")}</FieldLabel>
      <TextField
        theme={theme}
        accessibilityLabel={t("auth.welcome.name")}
        autoCapitalize="words"
        autoComplete="name"
        autoCorrect={false}
        editable={!busy}
        invalid={badField === "name"}
        placeholder={t("auth.welcome.namePlaceholder")}
        style={styles.field}
        value={name}
        onChangeText={setName}
      />

      <NewPasswordFields
        theme={theme}
        badField={
          badField === "password" || badField === "confirm" ? badField : null
        }
        confirm={confirm}
        editable={!busy}
        password={password}
        revealed={revealed}
        onChangeConfirm={setConfirm}
        onChangePassword={setPassword}
        onToggleReveal={() => setRevealed((v) => !v)}
      />

      <AgeDeclarationCheckbox
        theme={theme}
        tickedAt={ageTickedAt}
        onChange={setAgeTickedAt}
        disabled={busy}
      />

      {error ? (
        <Text
          style={styles.error}
          accessibilityRole="alert"
          accessibilityLiveRegion="polite"
        >
          {error}
        </Text>
      ) : null}

      <Button
        theme={theme}
        label={t("auth.welcome.submit")}
        loading={busy}
        onPress={onSubmit}
      />
    </View>
  );
}

// ⛔ Nessuno stile PROPRIO rimasto: le righe delle regole se ne sono andate con
// il blocco condiviso (`NewPasswordFields`), e la colonna viene dal modulo che
// serve tutte le porte. Resta un alias, non un `makeStyles` vuoto che sembra
// aspettare qualcosa.
const makeStyles = authFormStyles;
